import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { getAllContacts, patchContact } from '../services/ContactService';
import { extractApiError } from '../utils/apiError';
import '../styles/CompanyDashboard.css';
import { useToast, useConfirm } from '../context/UIContext';

export default function Contacts() {
    const toast = useToast();
    const confirm = useConfirm();
    const [companies, setCompanies]           = useState([]);
    const [contacts, setContacts]             = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [searchTerm, setSearchTerm]         = useState('');
    const [showAddForm, setShowAddForm]       = useState(false);
    const [selectedDetails, setSelectedDetails] = useState(null);
    const [editContact, setEditContact]       = useState(null);

    const EMPTY_CONTACT = { name: '', position: '', email: '', phone: '', mobile: '' };
    const [newContact, setNewContact]   = useState(EMPTY_CONTACT);
    const [detailPairs, setDetailPairs] = useState([{ key: '', value: '' }]);

    useEffect(() => {
        api.get('/Company').then(res => setCompanies(res.data));
        getAllContacts().then(data => setContacts(data));
    }, []);

    const loadContacts = () => getAllContacts().then(data => setContacts(data));

    const selectedCompany = companies.find(c => c.id === selectedCompanyId);

    const filteredCompanies = companies.filter(c =>
        c.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredContacts = selectedCompanyId
        ? contacts.filter(c => c.companyId === selectedCompanyId)
        : [];

    const setField = (field) => (e) => setNewContact({ ...newContact, [field]: e.target.value });

    const handleDetailChange = (index, field, value) => {
        const updated = [...detailPairs];
        updated[index][field] = value;
        setDetailPairs(updated);
    };

    const handleAddContact = () => {
        if (!selectedCompanyId || !newContact.name) return;
        const detailsJson = {};
        detailPairs.forEach(p => { if (p.key.trim()) detailsJson[p.key] = p.value; });
        api.post('/Contact', {
            name: newContact.name,
            position: newContact.position,
            email: newContact.email,
            phone_number: newContact.phone,
            mobile_number: newContact.mobile,
            company_id: selectedCompanyId,
            details: JSON.stringify(detailsJson),
        }).then(() => {
            setNewContact(EMPTY_CONTACT);
            setDetailPairs([{ key: '', value: '' }]);
            setShowAddForm(false);
            loadContacts();
        }).catch(err => { toast(extractApiError(err, 'Fehler beim Erstellen des Kontakts.')); });
    };

    const handleDeleteContact = async (id) => {
        if (await confirm('Kontakt wirklich löschen?', { confirmLabel: 'Löschen', danger: true }))
            api.delete(`/Contact/${id}`).then(loadContacts);
    };

    const handleEditContact = (contact) => {
        const details = parseDetails(contact.details);
        const pairs = Object.entries(details).map(([key, value]) => ({ key, value: String(value) }));
        setNewContact({
            name:     contact.name     || '',
            position: contact.position || '',
            email:    contact.eMail    || '',
            phone:    contact.phoneNumber  || '',
            mobile:   contact.mobileNumber || '',
        });
        setDetailPairs(pairs.length > 0 ? pairs : [{ key: '', value: '' }]);
        setEditContact(contact);
        setShowAddForm(true);
        setSelectedDetails(null);
    };

    const handleSaveEdit = () => {
        if (!editContact) return;
        const detailsJson = {};
        detailPairs.forEach(p => { if (p.key.trim()) detailsJson[p.key] = p.value; });
        patchContact(editContact.id, {
            name:          newContact.name,
            position:      newContact.position,
            email:         newContact.email,
            phone_number:  newContact.phone,
            mobile_number: newContact.mobile,
            details:       Object.keys(detailsJson).length > 0 ? JSON.stringify(detailsJson) : null,
        }).then(() => {
            setNewContact(EMPTY_CONTACT);
            setDetailPairs([{ key: '', value: '' }]);
            setEditContact(null);
            setShowAddForm(false);
            loadContacts();
        }).catch(err => { toast(extractApiError(err, 'Fehler beim Speichern des Kontakts.')); });
    };

    const handleCancelForm = () => {
        setShowAddForm(false);
        setEditContact(null);
        setNewContact(EMPTY_CONTACT);
        setDetailPairs([{ key: '', value: '' }]);
    };

    const parseDetails = (raw) => {
        if (!raw) return {};
        if (typeof raw === 'object') return raw;
        try { return JSON.parse(raw); } catch { return {}; }
    };

    const handleShowDetails = (contact) => {
        setSelectedDetails({ contact, parsed: parseDetails(contact.details) });
    };

    const handleSelectCompany = (id) => {
        setSelectedCompanyId(id);
        setShowAddForm(false);
        setEditContact(null);
        setSelectedDetails(null);
        setNewContact(EMPTY_CONTACT);
        setDetailPairs([{ key: '', value: '' }]);
    };

    return (
        <div className="contacts-layout">

            <aside className="contacts-sidebar">
                <div className="contacts-sidebar-header">
                    <h2 className="contacts-sidebar-title">Kunden</h2>
                    <span className="contacts-sidebar-count">{companies.length}</span>
                </div>

                <div className="contacts-search-wrap">
                    <svg className="contacts-search-icon" viewBox="0 0 20 20" fill="none">
                        <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.75"/>
                        <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
                    </svg>
                    <input
                        className="contacts-search"
                        type="text"
                        placeholder="Kunde suchen…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>

                <ul className="contacts-company-list">
                    {filteredCompanies.map(company => (
                        <li
                            key={company.id}
                            className={`contacts-company-item${company.id === selectedCompanyId ? ' selected' : ''}`}
                            onClick={() => handleSelectCompany(company.id)}
                        >
                            <div className="contacts-company-avatar">{company.name?.[0]?.toUpperCase() ?? '?'}</div>
                            <div className="contacts-company-info">
                                <span className="contacts-company-name">{company.name}</span>
                                {company.address && <span className="contacts-company-addr">{company.address}</span>}
                            </div>
                        </li>
                    ))}
                </ul>
            </aside>

            <main className="contacts-main">
                {!selectedCompanyId ? (
                    <div className="contacts-empty-state">
                        <div className="contacts-empty-icon">
                            <svg viewBox="0 0 48 48" fill="none">
                                <circle cx="24" cy="18" r="8" stroke="currentColor" strokeWidth="2.5"/>
                                <path d="M8 40c0-8.837 7.163-16 16-16s16 7.163 16 16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                            </svg>
                        </div>
                        <p className="contacts-empty-title">Keinen Kunden ausgewählt</p>
                        <p className="contacts-empty-sub">Wählen Sie links einen Kunden aus, um dessen Kontakte zu sehen.</p>
                    </div>
                ) : (
                    <>
                        <div className="contacts-section-header">
                            <div>
                                <h2 className="contacts-section-title">{selectedCompany?.name}</h2>
                                <p className="contacts-section-sub">
                                    {filteredContacts.length === 0
                                        ? 'Noch keine Kontakte'
                                        : `${filteredContacts.length} Kontakt${filteredContacts.length !== 1 ? 'e' : ''}`}
                                </p>
                            </div>
                            <button className="button" onClick={showAddForm ? handleCancelForm : () => setShowAddForm(true)}>
                                {showAddForm ? '✕ Abbrechen' : '+ Neuer Kontakt'}
                            </button>
                        </div>

                        {showAddForm && (
                            <div className="contacts-add-form">
                                <h4 className="contacts-form-heading">
                                    {editContact ? 'Kontakt bearbeiten' : 'Neuer Kontakt'}
                                </h4>
                                <div className="contacts-form-grid">
                                    {[
                                        { label: 'Name *', field: 'name', placeholder: 'Vor- und Nachname' },
                                        { label: 'Position', field: 'position', placeholder: 'z.B. Projektleiter' },
                                        { label: 'E-Mail', field: 'email', placeholder: 'name@firma.de' },
                                        { label: 'Telefon', field: 'phone', placeholder: '+49 …' },
                                        { label: 'Mobil', field: 'mobile', placeholder: '+49 …' },
                                    ].map(({ label, field, placeholder }) => (
                                        <div key={field} className="contacts-form-field">
                                            <label className="contacts-form-label">{label}</label>
                                            <input
                                                className="contacts-form-input"
                                                placeholder={placeholder}
                                                value={newContact[field]}
                                                onChange={setField(field)}
                                            />
                                        </div>
                                    ))}
                                </div>

                                <div className="contacts-details-section">
                                    <div className="contacts-details-header">
                                        <label className="contacts-form-label">Zusätzliche Details</label>
                                        <button className="contacts-add-detail-btn" onClick={() => setDetailPairs([...detailPairs, { key: '', value: '' }])}>
                                            + Zeile
                                        </button>
                                    </div>
                                    {detailPairs.map((pair, i) => (
                                        <div key={i} className="contacts-detail-row">
                                            <input
                                                className="contacts-form-input"
                                                placeholder="Bezeichnung"
                                                value={pair.key}
                                                onChange={e => handleDetailChange(i, 'key', e.target.value)}
                                            />
                                            <input
                                                className="contacts-form-input"
                                                placeholder="Wert"
                                                value={pair.value}
                                                onChange={e => handleDetailChange(i, 'value', e.target.value)}
                                            />
                                        </div>
                                    ))}
                                </div>

                                <div className="contacts-form-actions">
                                    <button className="button" onClick={editContact ? handleSaveEdit : handleAddContact}>
                                        {editContact ? 'Änderungen speichern' : 'Kontakt speichern'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {filteredContacts.length === 0 ? (
                            <p className="contacts-no-results">Keine Kontakte für diesen Kunden. Fügen Sie den Ersten hinzu.</p>
                        ) : (
                            <div className="contacts-cards-grid">
                                {filteredContacts.map(contact => (
                                    <div key={contact.id} className="contact-card">
                                        <div className="contact-card-header">
                                            <div className="contact-avatar">{contact.name?.[0]?.toUpperCase() ?? '?'}</div>
                                            <div>
                                                <div className="contact-card-name">{contact.name}</div>
                                                {contact.position && <div className="contact-card-position">{contact.position}</div>}
                                            </div>
                                        </div>

                                        <div className="contact-card-body">
                                            {contact.eMail && (
                                                <a href={`mailto:${contact.eMail}`} className="contact-detail-row">
                                                    <svg viewBox="0 0 20 20" fill="none" className="contact-detail-icon">
                                                        <rect x="2" y="5" width="16" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                                                        <path d="M2 7l8 5 8-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                                    </svg>
                                                    {contact.eMail}
                                                </a>
                                            )}
                                            {contact.phoneNumber && (
                                                <div className="contact-detail-row">
                                                    <svg viewBox="0 0 20 20" fill="none" className="contact-detail-icon">
                                                        <path d="M5 3h3l1.5 4-2 1.5a11 11 0 0 0 4 4L13 10.5 17 12v3a1 1 0 0 1-1 1C7.163 16 4 8.837 4 4a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                                                    </svg>
                                                    {contact.phoneNumber}
                                                </div>
                                            )}
                                            {contact.mobileNumber && (
                                                <div className="contact-detail-row">
                                                    <svg viewBox="0 0 20 20" fill="none" className="contact-detail-icon">
                                                        <rect x="6" y="2" width="8" height="16" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                                                        <circle cx="10" cy="15" r="0.75" fill="currentColor"/>
                                                    </svg>
                                                    {contact.mobileNumber}
                                                </div>
                                            )}
                                        </div>

                                        <div className="contact-card-actions">
                                            <button className="contact-action-btn" onClick={() => handleShowDetails(contact)}>
                                                Details
                                            </button>
                                            <button className="contact-action-btn" onClick={() => handleEditContact(contact)}>
                                                Bearbeiten
                                            </button>
                                            <button className="contact-action-btn contact-action-danger" onClick={() => handleDeleteContact(contact.id)}>
                                                Löschen
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </main>

            {selectedDetails && (
                <div className="contacts-details-panel">
                    <div className="contacts-details-panel-header">
                        <div className="contact-avatar">{selectedDetails.contact.name?.[0]?.toUpperCase()}</div>
                        <div>
                            <div className="contacts-details-panel-name">{selectedDetails.contact.name}</div>
                            {selectedDetails.contact.position && (
                                <div className="contacts-details-panel-sub">{selectedDetails.contact.position}</div>
                            )}
                        </div>
                        <button className="contacts-details-close" onClick={() => setSelectedDetails(null)}>
                            <svg viewBox="0 0 20 20" fill="none">
                                <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
                            </svg>
                        </button>
                    </div>
                    <div className="contacts-details-panel-body">

                        {selectedDetails.contact.eMail && (
                            <div className="contacts-detail-item">
                                <span className="contacts-detail-key">E-Mail</span>
                                <a href={`mailto:${selectedDetails.contact.eMail}`} className="contacts-detail-val contacts-detail-link">
                                    {selectedDetails.contact.eMail}
                                </a>
                            </div>
                        )}
                        {selectedDetails.contact.phoneNumber && (
                            <div className="contacts-detail-item">
                                <span className="contacts-detail-key">Telefon</span>
                                <a href={`tel:${selectedDetails.contact.phoneNumber}`} className="contacts-detail-val contacts-detail-link">
                                    {selectedDetails.contact.phoneNumber}
                                </a>
                            </div>
                        )}
                        {selectedDetails.contact.mobileNumber && (
                            <div className="contacts-detail-item">
                                <span className="contacts-detail-key">Mobil</span>
                                <a href={`tel:${selectedDetails.contact.mobileNumber}`} className="contacts-detail-val contacts-detail-link">
                                    {selectedDetails.contact.mobileNumber}
                                </a>
                            </div>
                        )}

                        {Object.entries(selectedDetails.parsed).length > 0 && (
                            <>
                                <div className="contacts-details-divider" />
                                {Object.entries(selectedDetails.parsed).map(([key, value], i) => (
                                    <div key={i} className="contacts-detail-item">
                                        <span className="contacts-detail-key">{key}</span>
                                        <span className="contacts-detail-val">{value}</span>
                                    </div>
                                ))}
                            </>
                        )}

                        {!selectedDetails.contact.eMail &&
                         !selectedDetails.contact.phoneNumber &&
                         !selectedDetails.contact.mobileNumber &&
                         Object.entries(selectedDetails.parsed).length === 0 && (
                            <p className="contacts-details-empty">Keine Details vorhanden.</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}